import { appendIf } from '@btime/helpers'
import { assoc, compose, path, pick, prop } from 'ramda'
import { gql, useMutation } from '@apollo/client'
import { useNotification } from '@btime/react-messager'
import { useTranslation } from 'react-i18next'

import { exportsClient } from '../../../../utils/apollo/api'
import { useCheckExport } from '../../../../utils/hooks/queueExport'
import { useCloseModal } from '../../../../components/modals/ModalsContext'

const cleanFilter = compose(
  pick(['ids']), //
  item => assoc('ids', prop('serviceOrderIds', item), item)
)

const cleanRequest = compose(
  pick(['filter', 'entity', 'fields']),
  item => assoc('filter', cleanFilter(item), item),
  item => assoc('entity', 'serviceOrder', item),
  item => assoc('fields', path(['template', 'configuration', 'fields'], item), item)
)

const validate = (form, setErrors, t) => {
  const errors = [
    ...appendIf(!form.serviceOrderIds, {
      path: ['serviceOrderIds'],
      message: t('export.validation.missingIds')
    }),
    ...appendIf(!form.template, { path: ['template'], message: t('export.validation.required') })
  ]

  setErrors(errors)
  return errors.length === 0
}

export const useExportSelectedServiceOrders = () => {
  const MUTATION = gql`
    mutation Export(
      $empty: Boolean
      $filter: ServiceOrderFilter
      $fields: [Dynamic]
      $entity: String
      $includeReplies: Boolean
    ) {
      exportServiceOrder(
        empty: $empty
        filter: $filter
        fields: $fields
        input: { entity: $entity }
        includeReplies: $includeReplies
      ) {
        id
        __typename
      }
    }
  `

  const [request, state] = useMutation(MUTATION, {
    refetchQueries: ['Exports'],
    client: exportsClient
  })

  const { t } = useTranslation('serviceOrders')
  const checkExport = useCheckExport()

  const notify = useNotification()
  const closeModal = useCloseModal()

  const handleRequest = (form, actions) => {
    const input = cleanRequest(form)
    const { setErrors } = actions
    const isValid = validate(form, setErrors, t)

    if (isValid) {
      const notificationId = notify.loading({ title: t('export.sendingRequest') })
      return request({ variables: input }).then(resp => {
        if (!resp.errors) {
          notify.updateToLoading(notificationId, { title: t('export.generatingFile') })
          checkExport(resp?.data[`exportServiceOrder`]?.id, notificationId)
          if (closeModal) closeModal()
        } else {
          notify.updateToError(notificationId, { title: t('export.error') })
        }
      })
    }
  }

  return [handleRequest, state]
}
